import { Component } from '@theme/component';

/**
 * Countdown timer custom element. Reads target date as wall-clock components in
 * America/Chicago (Central Time, DST-aware) and ticks down once per second.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [days]
 * @property {HTMLElement} [hours]
 * @property {HTMLElement} [minutes]
 * @property {HTMLElement} [seconds]
 * @property {HTMLElement} [timer]
 * @property {HTMLElement} [defaultText]
 * @property {HTMLElement} [completedText]
 *
 * @extends {Component<Refs>}
 */
export class CountdownTimer extends Component {
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #interval;

  connectedCallback() {
    super.connectedCallback();
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#stop();
  }

  #start() {
    const target = this.#chicagoWallClockToUtc(
      Number(this.dataset.year),
      Number(this.dataset.month),
      Number(this.dataset.day),
      Number(this.dataset.hour),
      Number(this.dataset.minute)
    );

    if (Number.isNaN(target)) return;

    const tick = () => {
      const distance = target - Date.now();

      if (distance <= 0) {
        this.#renderZero();
        this.#handleComplete();
        this.#stop();
        return;
      }

      const day = 86_400_000;
      const hour = 3_600_000;
      const minute = 60_000;
      const second = 1_000;

      // [FROST BUDDY] data-hide-days: no days box, so whole days roll into the hours.
      if (this.dataset.hideDays === 'true') {
        this.#set('hours', Math.floor(distance / hour));
      } else {
        this.#set('days', Math.floor(distance / day));
        this.#set('hours', Math.floor((distance % day) / hour));
      }
      this.#set('minutes', Math.floor((distance % hour) / minute));
      this.#set('seconds', Math.floor((distance % minute) / second));
    };

    tick();
    this.#interval = setInterval(tick, 1000);
  }

  #stop() {
    if (this.#interval !== undefined) {
      clearInterval(this.#interval);
      this.#interval = undefined;
    }
  }

  /**
   * @param {keyof Refs} refName
   * @param {number} value
   */
  #set(refName, value) {
    const el = this.refs[refName];
    if (el instanceof HTMLElement) {
      el.textContent = String(value).padStart(2, '0');
    }
  }

  #renderZero() {
    this.#set('days', 0);
    this.#set('hours', 0);
    this.#set('minutes', 0);
    this.#set('seconds', 0);
  }

  #handleComplete() {
    if (this.dataset.hideOnComplete === 'true' && this.refs.timer instanceof HTMLElement) {
      this.refs.timer.setAttribute('hidden', '');
    }

    const { defaultText, completedText } = this.refs;
    if (defaultText instanceof HTMLElement && completedText instanceof HTMLElement) {
      defaultText.setAttribute('hidden', '');
      completedText.removeAttribute('hidden');
    }
  }

  /**
   * Convert a wall-clock time in America/Chicago to a UTC epoch in ms.
   * Resolves CST vs CDT automatically via the runtime's IANA tz database.
   *
   * @param {number} year
   * @param {number} month - 1-12
   * @param {number} day
   * @param {number} hour - 0-23
   * @param {number} minute - 0-59
   * @returns {number}
   */
  #chicagoWallClockToUtc(year, month, day, hour, minute) {
    const naive = Date.UTC(year, month - 1, day, hour, minute);

    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

    /** @type {Record<string, string>} */
    const parts = Object.fromEntries(
      fmt.formatToParts(new Date(naive)).map((p) => [p.type, p.value])
    );

    const shown = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute)
    );

    return naive + (naive - shown);
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
